(() => {
  'use strict';

  const config = window.BoerenShotMaxOneConfig || {};
  const variantIds = new Set((config.variantIds || []).map((id) => String(id)));
  const productHandle = String(config.productHandle || 'boerensplit');
  const maxQuantity = Number(config.maxQuantity || 1);
  const errorMessage = String(
    config.errorMessage || 'Van BoerenSplit kan maximaal 1 fles per bestelling worden besteld.'
  );

  if (!variantIds.size) return;

  const nativeFetch = window.fetch.bind(window);
  let cachedCartQuantity = Number(config.cartQuantity || 0);
  let boerensplitAddQueue = Promise.resolve();

  const isBoerensplitVariant = (id) => variantIds.has(String(id || '').split(':')[0]);

  const requestUrl = (input) => {
    try {
      if (typeof input === 'string') return new URL(input, window.location.origin);
      if (input && typeof input.url === 'string') return new URL(input.url, window.location.origin);
      return new URL(String(input), window.location.origin);
    } catch (_) {
      return null;
    }
  };

  const routeMatches = (url, route) => {
    if (!url) return false;
    return new RegExp(`/(?:[a-z]{2}(?:-[a-z]{2})?/)?cart/${route}(?:\\.js)?/?$`, 'i').test(url.pathname);
  };

  const getBody = (input, init) => {
    if (init && Object.prototype.hasOwnProperty.call(init, 'body')) return init.body;
    return null;
  };

  const parseBody = (body) => {
    if (!body) return { type: 'none', value: null };

    if (body instanceof FormData) return { type: 'formdata', value: body };
    if (body instanceof URLSearchParams) return { type: 'urlsearchparams', value: body };

    if (typeof body === 'string') {
      try {
        return { type: 'json', value: JSON.parse(body) };
      } catch (_) {
        try {
          return { type: 'urlsearchparams', value: new URLSearchParams(body) };
        } catch (__) {
          return { type: 'text', value: body };
        }
      }
    }

    if (typeof body === 'object') return { type: 'object', value: body };
    return { type: 'unknown', value: body };
  };

  const readField = (parsed, name) => {
    if (!parsed) return null;
    if (parsed.type === 'formdata' || parsed.type === 'urlsearchparams') return parsed.value.get(name);
    if (parsed.type === 'json' || parsed.type === 'object') return parsed.value ? parsed.value[name] : null;
    return null;
  };

  const cloneInitWithField = (init, parsed, name, value) => {
    const nextInit = { ...(init || {}) };

    if (parsed.type === 'formdata') {
      const nextBody = new FormData();
      parsed.value.forEach((fieldValue, fieldName) => nextBody.append(fieldName, fieldValue));
      nextBody.set(name, String(value));
      nextInit.body = nextBody;
      return nextInit;
    }

    if (parsed.type === 'urlsearchparams') {
      const nextBody = new URLSearchParams(parsed.value.toString());
      nextBody.set(name, String(value));
      nextInit.body = nextBody;
      return nextInit;
    }

    if (parsed.type === 'json' || parsed.type === 'object') {
      const nextBody = { ...(parsed.value || {}), [name]: value };
      if (parsed.type === 'json') {
        nextInit.body = JSON.stringify(nextBody);
      } else {
        nextInit.body = nextBody;
      }
      return nextInit;
    }

    return nextInit;
  };

  const requestedBoerensplitQuantity = (parsed) => {
    if (!parsed || !parsed.value) return 0;

    if (parsed.type === 'formdata' || parsed.type === 'urlsearchparams') {
      const id = parsed.value.get('id');
      if (!isBoerensplitVariant(id)) return 0;
      return Math.max(1, Number(parsed.value.get('quantity') || 1));
    }

    if (parsed.type === 'json' || parsed.type === 'object') {
      const data = parsed.value;
      if (Array.isArray(data.items)) {
        return data.items.reduce((total, item) => {
          if (!item || !isBoerensplitVariant(item.id)) return total;
          return total + Math.max(1, Number(item.quantity || 1));
        }, 0);
      }
      if (isBoerensplitVariant(data.id)) return Math.max(1, Number(data.quantity || 1));
    }

    return 0;
  };

  const cartQuantityFromData = (cartData) => {
    if (!cartData || !Array.isArray(cartData.items)) return cachedCartQuantity;
    return cartData.items.reduce((total, item) => {
      if (!item || !isBoerensplitVariant(item.variant_id || item.id)) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  };

  const getFreshCart = async () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await nativeFetch(`${root}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart could not be loaded');
    const cartData = await response.json();
    cachedCartQuantity = cartQuantityFromData(cartData);
    refreshUi();
    return cartData;
  };

  const errorResponse = () => new Response(
    JSON.stringify({ status: 422, message: 'Cart Error', description: errorMessage }),
    {
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  const syncCartFromResponse = (response) => {
    if (!response || !response.ok || typeof response.clone !== 'function') return;
    response.clone().json().then((data) => {
      if (data && Array.isArray(data.items)) {
        cachedCartQuantity = cartQuantityFromData(data);
      } else if (data && isBoerensplitVariant(data.variant_id || data.id)) {
        cachedCartQuantity = Math.min(maxQuantity, Number(data.quantity || maxQuantity));
      }
      refreshUi();
    }).catch(() => {});
  };

  const guardedAdd = async (input, init, parsed) => {
    try {
      const requestedQuantity = requestedBoerensplitQuantity(parsed);
      if (!requestedQuantity) return nativeFetch(input, init);

      await getFreshCart();
      if (cachedCartQuantity + requestedQuantity > maxQuantity) {
        showInlineMessage(errorMessage);
        return errorResponse();
      }

      const response = await nativeFetch(input, init);
      if (response.ok) cachedCartQuantity = Math.min(maxQuantity, cachedCartQuantity + requestedQuantity);
      syncCartFromResponse(response);
      refreshUi();
      return response;
    } catch (_) {
      return nativeFetch(input, init);
    }
  };

  const guardedChange = async (input, init, parsed) => {
    const quantity = Number(readField(parsed, 'quantity'));
    if (!Number.isFinite(quantity) || quantity <= maxQuantity) {
      const response = await nativeFetch(input, init);
      syncCartFromResponse(response);
      return response;
    }

    try {
      const cartData = await getFreshCart();
      const id = readField(parsed, 'id');
      const line = Number(readField(parsed, 'line'));
      let targetsBoerensplit = isBoerensplitVariant(id);

      if (!targetsBoerensplit && Number.isInteger(line) && line > 0 && cartData.items[line - 1]) {
        targetsBoerensplit = isBoerensplitVariant(cartData.items[line - 1].variant_id);
      }

      if (targetsBoerensplit) {
        const guardedInit = cloneInitWithField(init, parsed, 'quantity', maxQuantity);
        showInlineMessage(errorMessage);
        const response = await nativeFetch(input, guardedInit);
        syncCartFromResponse(response);
        return response;
      }
    } catch (_) {
      // Fall through to the theme's original request when cart inspection fails.
    }

    const response = await nativeFetch(input, init);
    syncCartFromResponse(response);
    return response;
  };

  window.fetch = function boerensplitMaxOneFetch(input, init) {
    const url = requestUrl(input);
    const parsed = parseBody(getBody(input, init));

    if (routeMatches(url, 'add') && requestedBoerensplitQuantity(parsed) > 0) {
      const run = boerensplitAddQueue.then(
        () => guardedAdd(input, init, parsed),
        () => guardedAdd(input, init, parsed)
      );
      boerensplitAddQueue = run.then(() => undefined, () => undefined);
      return run;
    }

    if (routeMatches(url, 'change')) return guardedChange(input, init, parsed);

    const responsePromise = nativeFetch(input, init);
    if (routeMatches(url, 'update') || routeMatches(url, 'clear')) {
      responsePromise.then(syncCartFromResponse).catch(() => {});
    }
    return responsePromise;
  };

  const normalizeCartToMaxOne = async (cartData) => {
    if (!cartData || !Array.isArray(cartData.items)) return cartData;

    let remaining = maxQuantity;
    const updates = {};

    cartData.items.forEach((item) => {
      if (!item || !isBoerensplitVariant(item.variant_id || item.id)) return;
      const current = Number(item.quantity || 0);
      const allowed = Math.max(0, Math.min(current, remaining));
      remaining -= allowed;
      if (allowed !== current && item.key) updates[item.key] = allowed;
    });

    if (!Object.keys(updates).length) return cartData;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await nativeFetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) throw new Error('BoerenSplit quantity could not be corrected');
    const normalizedCart = await response.json();
    cachedCartQuantity = cartQuantityFromData(normalizedCart);
    refreshUi();
    return normalizedCart;
  };

  const associatedVariantId = (element) => {
    if (!element) return null;

    const ownInput = element.querySelector && element.querySelector('input[name="id"]');
    if (ownInput) return ownInput.value;

    const formId = element.getAttribute && element.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      const input = form && form.querySelector('input[name="id"]');
      if (input) return input.value;
    }

    return null;
  };

  const rememberLabel = (button) => {
    const label = button.querySelector('.main-atc__label__text, .sticky-atc__label__text');
    if (!label) return;
    if (!label.dataset.maxOneOriginalText) label.dataset.maxOneOriginalText = label.textContent.trim();
  };

  const setButtonGuardState = (button, shouldDisable) => {
    if (!button) return;
    rememberLabel(button);
    const label = button.querySelector('.main-atc__label__text, .sticky-atc__label__text');

    if (shouldDisable) {
      if (!button.disabled) button.dataset.maxOneDisabledByGuard = 'true';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      if (label && !button.dataset.unavailable && label.textContent.trim() !== 'MAXIMAAL 1 PER BESTELLING') {
        label.textContent = 'MAXIMAAL 1 PER BESTELLING';
      }
    } else if (button.dataset.maxOneDisabledByGuard === 'true') {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      delete button.dataset.maxOneDisabledByGuard;
      if (label && label.dataset.maxOneOriginalText && label.textContent.trim() !== label.dataset.maxOneOriginalText) {
        label.textContent = label.dataset.maxOneOriginalText;
      }
    }
  };

  const limitProductForms = () => {
    document.querySelectorAll('product-form').forEach((productForm) => {
      const idInput = productForm.querySelector('input[name="id"]');
      if (!idInput || !isBoerensplitVariant(idInput.value)) return;

      productForm.dataset.maxOneProduct = 'true';
      const form = idInput.form || productForm.querySelector('form');
      if (form && form.id) {
        document.querySelectorAll('input[name="quantity"][form]').forEach((input) => {
          if (input.getAttribute('form') !== form.id) return;
          input.max = String(maxQuantity);
          input.setAttribute('data-max', String(maxQuantity));
          if (Number(input.value) > maxQuantity) input.value = String(maxQuantity);
          const quantityInput = input.closest('quantity-input');
          const plus = quantityInput && quantityInput.querySelector('button[name="plus"]');
          if (plus) plus.disabled = true;
        });
      }

      productForm.querySelectorAll('button[type="submit"], button[name="add"]').forEach((button) => {
        setButtonGuardState(button, cachedCartQuantity >= maxQuantity);
      });
    });

    document.querySelectorAll('button[form]').forEach((button) => {
      if (!isBoerensplitVariant(associatedVariantId(button))) return;
      setButtonGuardState(button, cachedCartQuantity >= maxQuantity);
    });
  };

  const limitCartRows = () => {
    document.querySelectorAll(`.cart-item--product-${productHandle}`).forEach((row) => {
      row.querySelectorAll('.quantity__input').forEach((input) => {
        input.max = String(maxQuantity);
        input.setAttribute('data-max', String(maxQuantity));
        if (Number(input.value) > maxQuantity) input.value = String(maxQuantity);
      });
      row.querySelectorAll('button[name="plus"]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.title = errorMessage;
      });
    });
  };

  function refreshUi() {
    limitProductForms();
    limitCartRows();
  }

  function showInlineMessage(message) {
    document.querySelectorAll('product-form[data-max-one-product="true"]').forEach((productForm) => {
      const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
      const text = productForm.querySelector('.product-form__error-message');
      if (wrapper && text) {
        text.textContent = message;
        wrapper.hidden = false;
      }
    });
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput || !isBoerensplitVariant(idInput.value)) return;

    const quantityInput = form.querySelector('input[name="quantity"]') ||
      (form.id ? Array.from(document.querySelectorAll('input[name="quantity"][form]')).find((input) => input.getAttribute('form') === form.id) : null);

    if (quantityInput && Number(quantityInput.value) > maxQuantity) {
      quantityInput.value = String(maxQuantity);
    }

    if (cachedCartQuantity >= maxQuantity) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showInlineMessage(errorMessage);
    }
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target.closest && event.target.closest(`.cart-item--product-${productHandle} .quantity__input`);
    if (!input) return;
    if (Number(input.value) > maxQuantity) input.value = String(maxQuantity);
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest(`.cart-item--product-${productHandle} .quantity__input`);
    if (!input) return;
    if (Number(input.value) > maxQuantity) input.value = String(maxQuantity);
  }, true);

  document.addEventListener('click', (event) => {
    const plus = event.target.closest && event.target.closest(`.cart-item--product-${productHandle} button[name="plus"]`);
    if (!plus) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showInlineMessage(errorMessage);
  }, true);

  document.addEventListener('change', () => setTimeout(refreshUi, 0), true);
  document.addEventListener('shopify:section:load', refreshUi);
  document.addEventListener('shopify:section:reorder', refreshUi);

  document.addEventListener('click', async (event) => {
    const checkoutButton = event.target.closest && event.target.closest('[name="checkout"]');
    if (!checkoutButton || checkoutButton.dataset.maxOneCheckoutBypass === 'true') return;
    if (cachedCartQuantity <= maxQuantity) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      const cartData = await getFreshCart();
      if (cartQuantityFromData(cartData) > maxQuantity) {
        await normalizeCartToMaxOne(cartData);
      }

      checkoutButton.dataset.maxOneCheckoutBypass = 'true';
      const form = checkoutButton.form;
      if (form) {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(checkoutButton);
        else form.submit();
      } else {
        window.location.assign('/checkout');
      }
    } catch (_) {
      showInlineMessage(errorMessage);
      window.alert(errorMessage);
    }
  }, true);

  const observer = new MutationObserver(() => refreshUi());
  const start = () => {
    refreshUi();
    getFreshCart().catch(() => refreshUi());
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
